import { t, Trans } from '@lingui/macro'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import {
    getOpportunities,
    opportunityFieldSets,
    opportunityListFields
} from 'app/services/sfAuth/sfData/sfOpportunity'
import { useOrganizationValidityBase } from 'app/services/sfAuth/sfHooks/useOrganizationValidityBase'
import { ActionsColumnHeader } from 'app/views/common-components/ActionsColumnHeader'
import CustomDataTable from 'app/views/common-components/CustomDataTable'
import { IncompleteOrganizationDetails } from 'app/views/common/alerts/IncompleteOrganizationDetails'
import { NotConnectedToOrgBanner } from 'app/views/common/alerts/NotConnectedToOrgBanner'
import { PortalTabHeader } from 'app/views/common/PortalTabHeader'
import { getLabelFromTranslationData } from 'app/views/common/translationsCommon'
import { matchOpportunityWithFundingStream } from 'app/views/internal/funding-streams/ConfigureFundingStreams'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { myI18n } from 'translation/I18nConnectedProvider'
import { muiDateCompare } from 'utils'
import Loading from '../../../../egret/components/EgretLoadable/Loading'
import { dateFormat } from '../../../appSettings'
import { muiTextLabels } from '../../utilities/muiDataTablesTranslations'
import { GRANTEE_REPORT_COMPLETED_STAGES, GRANTEE_REPORT_STATUSES } from './GranteeReportData'
import ReportActions from './ReportActions'
import { mapReportsForDataTable } from './ReportsUtils'

const Reports = ({ ...props }) => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const user = useSelector((state) => state.user)
  const organization = useSelector((state) => state.organization)
  const { fundingStreams } = useGlobalQueries()
  const config = useSelector((state) => state.configuration)
  const simulateInternal = useSelector(
    (state) => state?.testingOptions?.simulateInternal
  )
  const isInternal = simulateInternal || sfOauthConfig.isInternal
  const { isLoadedAndInvalid: orgDetailsInvalid } = useOrganizationValidityBase({
    accountId: organization.id,
    userId: user.userId,
    formId: config.FORM_ORGANIZATION_DETAILS
  })

  const fetchData = () => {
    setLoading(true)
    return getOpportunities({
      permissions: opportunityFieldSets.REPORTS,
      fields: [...opportunityListFields, 'Id', 'StageName']
    }).then(
      (opportunities) => {
        const reports = mapReportsForDataTable({
          fundingStreams,
          isInternal,
          opportunities,
          organization,
          user
        })
        setData(reports)
        setLoading(false)
      },
      (e) => {
        console.log(e)
        setLoading(false)
      }
    )
  }

  useEffect(() => {
    fetchData()
  }, [organization.id])

  useEffect(() => {
    if (data) {
      const toSet = [...data]
      toSet.map((report) => {
        const fundingStream = matchOpportunityWithFundingStream({
          fundingStreams,
          fundingStreamId: report.fundingStreamId
        })
        if (fundingStream) {
          report.fundingStream = getLabelFromTranslationData({
            data: fundingStream.fullName,
            langVersion: user.language
          })
        }
        return report
      })
      setData(toSet)
    }
  }, [user.language])

  if (loading) {
    return <Loading atCenter />
  }

  return (
    <>
      {orgDetailsInvalid && <IncompleteOrganizationDetails />}
      {(!organization.id) && <NotConnectedToOrgBanner />}
      <div className='portal-tab-container'>
        <PortalTabHeader
          header={<Trans>Reports</Trans>}
        />
        <CustomDataTable
          data={data}
          options={{
            textLabels: muiTextLabels(myI18n),
            filter: false,
            selectableRows: 'none',
            print: false,
            download: false,
            viewColumns: false,
            sortOrder: {
              name: 'due',
              direction: 'asc'
            }
          }}
          columns={[
            {
              name: 'name',
              label: myI18n._(t`Report name`),
              options: {
                customBodyRender: (value, tableMeta, updateValue) => {
                  return <div style={{ flex: 1 }}>{value}</div>
                }
              }
            },
            {
              name: 'opportunity',
              label: myI18n._(t`Associated opportunity name`),
              options: {
                customBodyRender: (value, tableMeta, updateValue) => {
                  return <div style={{ flex: 1 }}>{value}</div>
                }
              }
            },
            {
              name: 'fundingStream',
              label: myI18n._(t`Fund`),
              options: {
                customBodyRender: (value, tableMeta, updateValue) => value
              }
            },
            {
              name: 'reportType',
              label: myI18n._(t`Type`),
              options: {
                customBodyRender: (value, tableMeta, updateValue) => {
                  return (
                    <div style={{ flex: 1 }}>
                      <Trans id={value} />
                    </div>
                  )
                }
              }
            },
            {
              name: 'status',
              label: myI18n._(t`Status`),
              options: {
                customBodyRender: (value, tableMeta, updateValue) => {
                  return (
                    <div style={{ flex: 1 }}>
                      <Trans id={value} />
                    </div>
                  )
                }
              }
            },
            {
              name: 'due',
              label: myI18n._(t`Due Date`),
              options: {
                sortCompare: muiDateCompare('dueDate'),
                customBodyRender: (value, tableMeta, updateValue) => {
                  return (
                    <div
                      style={{
                        flex: 1,
                        color: value.isOverdue ? 'red' : 'inherit'
                      }}
                    >
                      {value.dueDate
                        ? moment.utc(value.dueDate).format(dateFormat)
                        : ''}
                    </div>
                  )
                }
              }
            },
            {
              name: 'actions',
              label: myI18n._(t`Actions`),
              options: {
                customHeadLabelRender: () => <ActionsColumnHeader />,
                sort: false,
                customBodyRender: (value, tableMeta, updateValue) => {
                  const { id, isEditable, status } = value
                  const isCompleted = GRANTEE_REPORT_COMPLETED_STAGES.includes(status)

                  return (
                    <ReportActions
                      id={id}
                      isEditable={isEditable && !isCompleted && status !== GRANTEE_REPORT_STATUSES.SCHEDULED}
                      history={history}
                    />
                  )
                }
              }
            }
          ]}
        />
      </div>
    </>

  )
}

export default Reports
